import { useMemo, useState } from "react";

export interface PlotMapGenerationData {
  rows: number;
  columns: number;
  totalPlots: number;
}

export const MAX_TOTAL_PLOTS = 50000;

export interface UsePlotMapGenerationReturn {
  // State
  plotRows: string;
  plotColumns: string;
  plotCount: string;
  customizePlotNames: boolean;
  customPlotNames: string[];
  plotMapSource: "upload" | "generate" | "sequence";

  // Setters
  setPlotRows: (rows: string) => void;
  setPlotColumns: (columns: string) => void;
  setPlotCount: (count: string) => void;
  setCustomizePlotNames: (customize: boolean) => void;
  setCustomPlotNames: (names: string[]) => void;
  setPlotMapSource: (source: "upload" | "generate" | "sequence") => void;

  // Computed values
  generationData: PlotMapGenerationData | null;
  isValidGeneration: boolean;
  isValidSequence: boolean;
  exceedsMaxPlots: boolean;
  duplicateNameIndices: number[];

  // Reset function
  resetGeneration: () => void;
}

/**
 * Hook for managing plot map generation state and validation
 * Provides data that an engineer can use to implement actual plot map generation
 */
export const usePlotMapGeneration = (): UsePlotMapGenerationReturn => {
  const [plotRows, setPlotRows] = useState<string>("");
  const [plotColumns, setPlotColumns] = useState<string>("");
  const [plotCount, setPlotCount] = useState<string>("");
  const [customizePlotNames, setCustomizePlotNames] = useState(false);
  const [customPlotNames, setCustomPlotNames] = useState<string[]>([]);
  const [plotMapSource, setPlotMapSource] = useState<"upload" | "generate" | "sequence">("upload");

  // Compute generation data
  const generationData: PlotMapGenerationData | null = (() => {
    if (plotMapSource !== "generate" || !plotRows || !plotColumns) {
      return null;
    }

    const rows = parseInt(plotRows);
    const columns = parseInt(plotColumns);

    if (isNaN(rows) || isNaN(columns) || rows < 1 || columns < 1) {
      return null;
    }

    return {
      rows,
      columns,
      totalPlots: rows * columns,
    };
  })();

  // Check if total plots exceeds maximum
  const exceedsMaxPlots = (() => {
    if (plotMapSource === "generate" && generationData !== null) {
      return generationData.totalPlots > MAX_TOTAL_PLOTS;
    }
    if (plotMapSource === "sequence" && plotCount) {
      const count = parseInt(plotCount);
      return !isNaN(count) && count > MAX_TOTAL_PLOTS;
    }
    return false;
  })();

  // Helper to find duplicate indices
  const duplicateNameIndices = useMemo(() => {
    const seen = new Map<string, number>();
    const duplicates: number[] = [];

    customPlotNames.forEach((name, index) => {
      const trimmed = name?.trim();
      if (trimmed) {
        if (seen.has(trimmed)) {
          duplicates.push(index);
          duplicates.push(seen.get(trimmed)!);
        } else {
          seen.set(trimmed, index);
        }
      }
    });

    return [...new Set(duplicates)].sort((a, b) => a - b);
  }, [customPlotNames]);

  // Validate sequence generation
  const isValidSequence = useMemo(() => {
    if (plotMapSource !== "sequence") {
      return true; // Not using sequence, so it's valid
    }

    // Parse and validate count is numeric
    const count = parseInt(plotCount);
    if (!plotCount || isNaN(count) || count < 1) return false;
    if (count > MAX_TOTAL_PLOTS) return false;

    if (customizePlotNames) {
      // All names must be provided
      if (customPlotNames.some(n => !n?.trim())) return false;
      // All names must be unique
      if (duplicateNameIndices.length > 0) return false;
    }

    return true;
  }, [plotCount, customizePlotNames, customPlotNames, duplicateNameIndices, plotMapSource]);

  // Validate generation parameters
  const isValidGeneration = (() => {
    if (plotMapSource !== "generate") {
      return true; // Not using generation, so it's valid
    }

    return generationData !== null &&
           generationData.rows >= 1 &&
           generationData.columns >= 1 &&
           !exceedsMaxPlots;
  })();

  // Reset function
  const resetGeneration = () => {
    setPlotRows("");
    setPlotColumns("");
    setPlotCount("");
    setCustomizePlotNames(false);
    setCustomPlotNames([]);
    setPlotMapSource("upload");
  };

  return {
    plotRows,
    plotColumns,
    plotCount,
    customizePlotNames,
    customPlotNames,
    plotMapSource,
    setPlotRows,
    setPlotColumns,
    setPlotCount,
    setCustomizePlotNames,
    setCustomPlotNames,
    setPlotMapSource,
    generationData,
    isValidGeneration,
    isValidSequence,
    exceedsMaxPlots,
    duplicateNameIndices,
    resetGeneration,
  };
};